// WPS-015 (admin) · Brand Pairings tab
// 44 looks curados (22 cat × 2 styles) — pre-confirmed visual combinations.
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Edit, Save, X, Sparkles } from 'lucide-react';
import { pairingsApi } from '../../services/api';

const fetchCategories = async () => {
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';
  return fetch(`${base}/categories`).then(r => r.json()).catch(() => []);
};

const BrandPairings = () => {
  const [pairings, setPairings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const [p, c] = await Promise.all([
      pairingsApi.list(),
      fetchCategories(),
    ]);
    setPairings(p || []);
    setCategories(c || []);
  };

  useEffect(() => { reload(); }, []);

  const updateColor = (idx, value) => {
    const colorPalette = [...(editing.colorPalette || ['#000000', '#c9a961', '#ffffff'])];
    colorPalette[idx] = value;
    setEditing({ ...editing, colorPalette });
  };

  const save = async () => {
    if (!editing?.id) return;
    const payload = { ...editing };
    delete payload._id;
    const r = await pairingsApi.update(editing.id, payload);
    if (r?.success) {
      setEditing(null);
      await reload();
    } else {
      alert('Save failed');
    }
  };

  const filtered = filter === 'all' ? pairings : pairings.filter(p => p.categorySlug === filter);

  const byCategory = {};
  filtered.forEach(p => {
    const k = p.categorySlug || 'other';
    if (!byCategory[k]) byCategory[k] = [];
    byCategory[k].push(p);
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" />
              Brand Pairings · 44 Curated Looks
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Pre-confirmed visual combinations · client picks a complete look in one click
            </p>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 px-2 py-1 text-sm font-mono"
          >
            <option value="all">All categories ({pairings.length})</option>
            {categories.map(c => (
              <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {Object.entries(byCategory).map(([slug, items]) => {
          const cat = categories.find(c => c.slug === slug);
          return (
            <div key={slug} className="mb-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                <span className="text-xl">{cat?.icon || '·'}</span>
                <h3 className="font-semibold">{cat?.name || slug}</h3>
                <span className="text-xs font-mono text-gray-500 uppercase tracking-wider ml-2">
                  {items.length} looks
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map(p => (
                  <div key={p.id} className="border border-gray-200 p-4 hover:border-blue-400 transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-semibold text-sm">{p.name}</div>
                        <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mt-1">
                          {p.style}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setEditing({ ...p })}>
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex gap-1 mb-2">
                      {(p.colorPalette || []).slice(0, 3).map((c, i) => (
                        <div key={i} style={{
                          width: 20, height: 20, backgroundColor: c,
                          border: '1px solid #e2e8f0',
                        }} title={c} />
                      ))}
                    </div>
                    {p.defaultSlogan && (
                      <div className="text-xs italic text-gray-600 mt-2">"{p.defaultSlogan}"</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {pairings.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>No pairings yet · they seed automatically on backend startup.</p>
          </div>
        )}

        {/* Edit dialog */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Brand Pairing</DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="space-y-3 py-2">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editing.name || ''}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Default Slogan</Label>
                  <Input
                    value={editing.defaultSlogan || ''}
                    onChange={(e) => setEditing({ ...editing, defaultSlogan: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2].map(i => (
                    <div key={i}>
                      <Label className="text-xs">{['Primary', 'Accent', 'Neutral'][i]}</Label>
                      <div className="flex gap-1 mt-1">
                        <input type="color" value={editing.colorPalette?.[i] || '#000000'}
                          onChange={(e) => updateColor(i, e.target.value)}
                          className="w-8 h-8 border border-gray-300" />
                        <Input
                          value={editing.colorPalette?.[i] || ''}
                          onChange={(e) => updateColor(i, e.target.value)}
                          className="font-mono text-xs flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>
                <X className="h-4 w-4 mr-2" />Cancel
              </Button>
              <Button onClick={save} className="bg-blue-700 hover:bg-blue-800">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default BrandPairings;
